/**
 * GlobalView — 3D Tiles Service (§12.1)
 *
 * Loads 3D building tilesets into CesiumJS viewer.
 * Priority: Google Photorealistic 3D Tiles → Cesium OSM Buildings fallback.
 */

import * as Cesium from 'cesium';

const GOOGLE_TILES_URL =
  'https://tile.googleapis.com/v1/3dtiles/root.json';

/** Cesium Ion asset ID for OSM Buildings */
const OSM_BUILDINGS_ASSET_ID = 96188;

/**
 * Load Google Photorealistic 3D Tiles tileset.
 *
 * @param viewer CesiumJS Viewer instance
 * @param apiKey Google Maps API key (must have Map Tiles API enabled)
 * @returns The loaded tileset, or null if loading failed
 */
export async function loadGoogle3DTiles(
  viewer: Cesium.Viewer,
  apiKey: string,
): Promise<Cesium.Cesium3DTileset | null> {
  if (!apiKey) {
    console.warn('[tiles3d] No Google Maps API key — skipping 3D Tiles');
    return null;
  }

  try {
    const tileset = await Cesium.Cesium3DTileset.fromUrl(
      `${GOOGLE_TILES_URL}?key=${apiKey}`,
      {
        showCreditsOnScreen: true,
      },
    );

    viewer.scene.primitives.add(tileset);
    console.info('[tiles3d] Google Photorealistic 3D Tiles loaded');
    return tileset;
  } catch (err) {
    console.warn('[tiles3d] Failed to load Google 3D Tiles, using fallback:', err);
    return null;
  }
}

/**
 * Load Cesium OSM Buildings (Ion Asset 96188).
 * Free 3D buildings data from OpenStreetMap.
 */
export async function loadOsmBuildings(
  viewer: Cesium.Viewer,
): Promise<Cesium.Cesium3DTileset | null> {
  try {
    const tileset = await Cesium.Cesium3DTileset.fromIonAssetId(
      OSM_BUILDINGS_ASSET_ID,
    );

    // Style buildings with height-based coloring — light neutral tones
    tileset.style = new Cesium.Cesium3DTileStyle({
      color: {
        conditions: [
          ['${feature["cesium#estimatedHeight"]} >= 100', 'color("hsl(210, 15%, 80%)")'],
          ['${feature["cesium#estimatedHeight"]} >= 50', 'color("hsl(215, 12%, 72%)")'],
          ['${feature["cesium#estimatedHeight"]} >= 20', 'color("hsl(220, 10%, 65%)")'],
          ['true', 'color("hsl(220, 8%, 58%)")'],
        ],
      },
    });

    // Override lighting so buildings are always visible
    tileset.imageBasedLighting.imageBasedLightingFactor = new Cesium.Cartesian2(1.0, 1.0);
    tileset.lightColor = new Cesium.Cartesian3(2.0, 2.0, 2.0);

    viewer.scene.primitives.add(tileset);
    console.info('[tiles3d] Cesium OSM Buildings loaded');
    return tileset;
  } catch (err) {
    console.warn('[tiles3d] Failed to load OSM Buildings:', err);
    return null;
  }
}

/**
 * Set up Cesium World Terrain as fallback.
 */
export async function loadFallbackTerrain(viewer: Cesium.Viewer): Promise<void> {
  try {
    const terrain = await Cesium.CesiumTerrainProvider.fromIonAssetId(1);
    viewer.terrainProvider = terrain;
    console.info('[tiles3d] Cesium World Terrain loaded');
  } catch (err) {
    console.warn('[tiles3d] Failed to load fallback terrain:', err);
  }
}

